import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Calendar,
  FileText,
  DollarSign,
  Package,
  TrendingUp,
  Users,
} from "lucide-react";
interface sugarReport {
  sum_sugar_received_factories: number; // BB
  sum_sugar_delivered_farmers: number; // CC
  remaining_sugar_in_contractor_storage: number; // AA
  sum_sugar_price_received_factory: number; // DD
  sum_sugar_deposit_amount: number; // EE
  remaining_sugar_balance_contractor: number; // (not shown in image)
  total_sugar_due_farmers: number; // GG
  remaining_sugar_due_farmers: number; // HH
  total_sugar_amount_due_farmers: number; // JJ
  remaining_sugar_amount_due_farmers: number; // KK
  report_generated_at: string;
  crop_year_name: string;
}

const ReportWaste: React.FC = () => {
  const [report, setReport] = useState<sugarReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const cropID = Number(localStorage.getItem("crop_year_id"));
        const response = await axios.post<sugarReport>(
          `https://api.havirkesht.ir/report-sugar/?crop_year_id=${cropID}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("app_atkey")}`,
            },
          }
        );
        setReport(response.data);
      } catch (err) {
        setError("Failed to fetch sugar report");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);
  const formatNumber = (num: number | undefined) => {
    return num?.toLocaleString("fa-IR") || "۰";
  };

  if (loading) {
    return <div> در حال بارگذاری...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">خطا! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
            گزارش جامع شکر
          </h1>
          <div className="flex justify-center items-center gap-3 text-gray-500 dark:text-gray-400 text-sm">
            <div className="flex items-center">
              <Calendar className="ml-1 w-3.5 h-3.5" />
              <span>تاریخ گزارش: {report?.report_generated_at}</span>
            </div>
            <div className="flex items-center">
              <FileText className="ml-1 w-3.5 h-3.5" />
              <span>سال زراعی: {report?.crop_year_name}</span>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Financial Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="bg-blue-600 dark:bg-blue-700 px-3 py-2 flex items-center">
              <DollarSign className="text-white ml-1.5 w-4 h-4" />
              <h2 className="text-white font-medium text-sm">اطلاعات مالی</h2>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  جمع مبلغ دریافتی از کارخانجات بابت شکر
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.sum_sugar_price_received_factory)} تومان
                </p>
              </div>
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  جمع مبلغ واریزی به کشاورزان بابت شکر
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.sum_sugar_deposit_amount)} تومان
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  مانده مبلغ شکر در حساب پیمانکار
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.remaining_sugar_balance_contractor)}{" "}
                  تومان
                </p>
              </div>
            </div>
          </div>

          {/* Sugar Quantity */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="bg-green-600 dark:bg-green-700 px-3 py-2 flex items-center">
              <Package className="text-white ml-1.5 w-4 h-4" />
              <h2 className="text-white font-medium text-sm">مقادیر شکر</h2>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  جمع شکر دریافتی از کارخانجات
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.sum_sugar_received_factories)} کیلوگرم
                </p>
              </div>
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  جمع شکر تحویلی به کشاورزان
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.sum_sugar_delivered_farmers)} کیلوگرم
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  مانده شکر در انبار پیمانکار
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.remaining_sugar_in_contractor_storage)}{" "}
                  کیلوگرم
                </p>
              </div>
            </div>
          </div>

          {/* Farmers' Debt (Amount) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="bg-purple-600 dark:bg-purple-700 px-3 py-2 flex items-center">
              <TrendingUp className="text-white ml-1.5 w-4 h-4" />
              <h2 className="text-white font-medium text-sm">
                طلب کشاورزان (مبلغ)
              </h2>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  کل طلب مبلغ شکر کشاورزان
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.total_sugar_amount_due_farmers)} تومان
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  مانده مبلغ طلب شکر کشاورزان
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.remaining_sugar_amount_due_farmers)}{" "}
                  تومان
                </p>
              </div>
            </div>
          </div>

          {/* Farmers' Debt (Quantity) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="bg-orange-600 dark:bg-orange-700 px-3 py-2 flex items-center">
              <Users className="text-white ml-1.5 w-4 h-4" />
              <h2 className="text-white font-medium text-sm">
                طلب کشاورزان (مقدار)
              </h2>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="pb-2 border-b border-gray-50 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  کل طلب شکر کشاورزان
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.total_sugar_due_farmers)} کیلوگرم
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
                  مانده طلب شکر کشاورزان
                </p>
                <p className="font-semibold text-gray-700 dark:text-gray-200">
                  {formatNumber(report?.remaining_sugar_due_farmers)} کیلوگرم
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportWaste;
